import { containsInvariant } from "../../../../core/services/filter-util";
import { PayrollManualSubmissionModal } from "../../manual-requests/submission-form/modal/payroll-manual-submission-modal.component";
import { createPayPeriod, PayrollEntryManualSubmissionAdapter } from "../payroll-entry-manual-submission-adapter.service";
import { IQService, IPromise } from "angular";
import { StateService, Transition } from "angular-ui-router";
import { ScheduleEventService } from "../../../../core/services";
import { UtilityService } from "../../../../core/services/utility.service";
import { PayPeriodResponse, PayrollResponse } from "@herbjoy/apex-api-models";
import { PayrollFilters } from "./departments/filters-editor/payroll-entry-filters-editor.component";

export const departmentSectionEnum = {
    administrative: "admins",
    dept400: "sales",
    dept900: "contractors"
}

export const viewSectionEnum = {
    overview: "overview",
    guaranteed: "guaranteed",
    commissionPieces: "commissionPieces",
    totalCommission: "totalCommission",
    otherEarnings: "otherEarnings"
};

/** A view representing the v1 full editors. */
export const v1View = {
    value: "v1",
    text: "Full (v1)"
};

interface DepartmentSelect {
    value: string;
}

interface PayPeriodVM extends PayPeriodResponse {
    dirty: boolean;
}

export interface PayPeriodSections {
    admins: PayPeriodVM[];
    sales: PayPeriodVM[];
    contractors: PayPeriodVM[];
}

interface ResolvedState {
    searchTerm: string;
    payrollFilters: PayrollFilters;
    navEnabled: boolean;
    selectedDepartment: DepartmentSelect;
    selectedView: string;
    payPeriods: PayPeriodSections;
    page: number;
    views: any;
}

export class PayrollEntryNavigationService {
    constructor(private $q: IQService, private $uibModal: any, private $state: StateService,
        private payrollEntryManualSubmissionAdapter: PayrollEntryManualSubmissionAdapter,
        private scheduleEventService: ScheduleEventService,
        private utilityService: UtilityService) {
        'ngInject';
    }

    getDepartments(payroll) {
        const departments = [];
        if (payroll.OwnerPayrollEntries.length || payroll.OfficeAdminPayrollEntries.length) {
            departments.push({
                // This section is dept100 and dept200 combined.
                value: departmentSectionEnum.administrative,
                text: "Owner & Admins"
            });
        }
        if (payroll.SalesPayrollEntries.length) {
            departments.push({
                value: departmentSectionEnum.dept400,
                text: "Sales Reps",
            });
        }
        if (payroll.ContractorPayrollEntries.length) {
            departments.push({
                value: departmentSectionEnum.dept900,
                text: "Contractors"
            });
        }
        return this.$q.resolve(departments);
    }

    getSectionsForDepartment(payroll, department) {
        let entries;
        if (!department) {
            return this.$q.resolve([]);
        }
        const dept = department && department.value;
        switch (dept) {
            case departmentSectionEnum.administrative:
                entries = payroll.OwnerPayrollEntries
                    .concat(payroll.OfficeAdminPayrollEntries);
                break;
            case departmentSectionEnum.dept400:
                entries = payroll.SalesPayrollEntries;
                break;
            case departmentSectionEnum.dept900:
                entries = payroll.ContractorPayrollEntries;
                break;
            default:
                entries = [];
                break;
        }
        const sections = [];
        if (payroll.Locked) {
            // Show overview at the top once the payroll has been locked.
            sections.push({
                value: viewSectionEnum.overview,
                text: "Overview"
            });
        }

        const anyPieceCommissions = entries.some(p => p.CommissionPieces.length);
        if (department.value !== departmentSectionEnum.dept900) {
            sections.push({
                value: viewSectionEnum.guaranteed,
                text: "Guaranteed"
            });
        }
        if (anyPieceCommissions && department.value === departmentSectionEnum.dept400) {
            sections.push({
                value: viewSectionEnum.commissionPieces,
                text: "Pc. Commissions"
            });
        }
        if (department.value !== departmentSectionEnum.dept900) {
            // Dept 900 doesn't have earnings other than contractor payment,
            // which is editable on the overview.
            sections.push({
                value: viewSectionEnum.totalCommission,
                text: "Total Commissions"
            });
            sections.push({
                value: viewSectionEnum.otherEarnings,
                text: "Other Earnings"
            });
        }
        if (!payroll.Locked) {
            // Show overview at the bottom before the payroll has been submitted.
            sections.push({
                value: viewSectionEnum.overview,
                text: "Overview"
            });
        }
        return this.$q.resolve(sections);
    }

    /**
     * Search the payroll for employees matching the search term and return the results,
     * organized by department.
     * @param {any} payroll
     * @param {any} searchTerm
     */
    searchPayrollForEmployees(payroll: PayrollResponse, searchTerm: string, payrollFilters: PayrollFilters)
        : IPromise<PayPeriodSections> {
        const results: Partial<PayPeriodSections> = {};
        results[departmentSectionEnum.administrative] =
            payroll.OwnerPayrollEntries.filter(p => searchPayPeriod(p, searchTerm, payrollFilters))
                .concat(payroll.OfficeAdminPayrollEntries.filter(p => searchPayPeriod(p, searchTerm, payrollFilters)));
        results[departmentSectionEnum.dept400] = payroll.SalesPayrollEntries
            .filter(p => searchPayPeriod(p, searchTerm, payrollFilters));
        results[departmentSectionEnum.dept900] = payroll.ContractorPayrollEntries
            .filter(p => searchPayPeriod(p, searchTerm, payrollFilters));
        return this.$q.resolve(results as PayPeriodSections);
    }

    selectDepartment(departments, payPeriods, { department }) {
        // Filter possible departments according to whether there is a section on the payroll
        // and that section does not have all results filtered out.
        const filteredDepartments = departments.filter(p => payPeriods[p.value]
            && payPeriods[p.value].length);

        // Prefer requested department that includes the search term.
        return filteredDepartments.find(p => p.value === department)
            // Fallback to first qualifying department.
            || filteredDepartments[0]
            // Fallback again to requested department
            || departments.find(p => p.value === department)
            // Final fallback to first department.
            || departments[0];
    }

    selectView(views, { view }) {
        if (view === "v1") {
            return v1View;
        }
        return views.find(p => p.value === view) || views[0];
    }

    /**
     * Return the updated resolves resulting from a dynamic update to parameters.
     */
    resolveDynamic(newParams, transition: Transition) {
        const injector = transition.injector();
        const resolves: Partial<ResolvedState> = {};
        const payrollFilters: PayrollFilters = { ...transition.params().payrollFilters };
        resolves.payrollFilters = payrollFilters;
        resolves.searchTerm = newParams.searchTerm ?? transition.params().searchTerm;
        if (newParams.navEnabled !== undefined) {
            resolves.navEnabled = newParams.navEnabled;
        }
        if (newParams.filterByIncompleteEmployeeRegistration !== undefined) {
            resolves.payrollFilters.filterByIncompleteEmployeeRegistration =
                newParams.filterByIncompleteEmployeeRegistration;
        }

        let resolvesPromise = this.$q.resolve(resolves);
        if (newParams.searchTerm !== undefined || newParams.filterByIncompleteEmployeeRegistration !== undefined) {
            const payroll = injector.get("payrollCalculations");
            const payPeriods = this.searchPayrollForEmployees(payroll, resolves.searchTerm, payrollFilters);
            const departments = injector.get("departments");
            const { department, view } = transition.params();
            const page = injector.get("page");
            resolvesPromise = resolvesPromise
                // Update pay periods
                .then(() => payPeriods)
                .then(payPeriods => resolves.payPeriods = payPeriods)
                // Update selected department
                .then(payPeriods => {
                    resolves.selectedDepartment =
                        this.selectDepartment(departments, payPeriods, { department });
                    resolves.page = {
                        ...page,
                        pageNumber: 1,
                        entriesLength: payPeriods[resolves.selectedDepartment.value].length
                    };
                    return resolves.selectedDepartment;
                })
                // Update list of views.
                .then(selectedDepartment => this.getSectionsForDepartment(payroll, selectedDepartment))
                .then(views => resolves.views = views)
                // Update selected view.
                .then(views => resolves.selectedView = this.selectView(views, { view }))
                .then(() => resolves);
        }
        return resolvesPromise;
    }


    getPayrollEntriesByDepartment(department, payPeriods) {
        let entries = [];
        switch (department) {
            case 'sales':
                entries = payPeriods.sales;
                break;
            case 'admins':
                entries = payPeriods.admins;
                break;
            case 'contractors':
                entries = payPeriods.contractors;
                break;
        }
        return entries;
    }

    getPayrollEntriesByEmployee(payPeriods, employeeID) {
        const entries = payPeriods.OwnerPayrollEntries.filter(p => p.EmployeeID === employeeID)
            .concat(payPeriods.OfficeAdminPayrollEntries.filter(p => p.EmployeeID === employeeID))
            .concat(payPeriods.SalesPayrollEntries.filter(p => p.EmployeeID === employeeID))
            .concat(payPeriods.ContractorPayrollEntries.filter(p => p.EmployeeID === employeeID));
        return entries;
    }

    getVisibilityAggregates(payrollEntries) {
        const aggregates: any = {};
        aggregates.maxWeek = Math.max(...payrollEntries.map(p => p.WeekNumber));
        aggregates.commissionPieces = new Array(payrollEntries.reduce(function (max, entry) {
            return Math.max(max, entry.CommissionPieces.length);
        }, 0));

        for (const payrollEntry of payrollEntries) {

            // Decide aggregate scope
            for (const key of Object.keys(payrollEntry)) {
                if (key.startsWith("Show")) {
                    // Convert local key from pascal to camel case.
                    const showKey = key.replace(/^S/, "s");
                    aggregates[showKey] = aggregates[showKey] || payrollEntry[key];
                }
            }
        }
        return aggregates;
    }

    isDirty(payPeriods: PayPeriodSections) {
        const entries = getAllPayPeriods(payPeriods);
        return entries.some(p => p.dirty);
    }

    updatePayrollEntryWarning(payroll, payrollEntry) {
        const warnings = [];
        if (payrollEntry.TotalHoursWorked > 0) {
            if (payroll.ShowCell &&
                (payrollEntry.CellAllowance === null || payrollEntry.CellAllowance <= 0)) {
                payrollEntry.NoCellAllowance = true;
                warnings.push("Cell allowance is missing.");
            }
            if (payroll.ShowTip &&
                (payrollEntry.Tip === null || payrollEntry.Tip <= 0)) {
                payrollEntry.NoTip = true;
                warnings.push("Tip is missing.");
            }
            if (payrollEntry.SubtotalCommission === null || payrollEntry.SubtotalCommission === 0) {
                payrollEntry.NoCommission = true;
                warnings.push("Employee commission is missing.");
            }
        } else if (payrollEntry.SubtotalCommission > 0) {
            // Only warn about having no hours when the rep has commission entered
            payrollEntry.NoHours = true;
            if (payrollEntry.AnyUnapprovedHours) {
                warnings.push("Employee has no approved hours");
            } else {
                warnings.push("Employee has no hours");
            }
        }

        payrollEntry.Warnings = warnings;
        payrollEntry.HasWarnings = warnings.length > 0;
    }

    requestManual(payrollEntry, payroll, payrollEntries) {
        const payPeriod = createPayPeriod(payroll.PayrollID, payrollEntry);
        const manualRequest = this.payrollEntryManualSubmissionAdapter
            .createManualRequest(payrollEntry);

        const scheduleEventsLoaded = this.scheduleEventService.retrieveScheduleEvents({
            startDate: payroll.PayrollStartDate,
            endDate: payroll.PayrollEndDate,
            assignedCorporationIDs: [payroll.IcdCorporationID]
        });

        //Compose a new locked payroll that only has the requested payroll entry
        const filteredPayroll = {
            ...payroll,
            CanUpdate: false,
            Locked: true,
            StartDate: payroll.PayrollStartDate,
            EndDate: payroll.PayrollEndDate,
            PayrollEntries: payrollEntries.filter(p => p.EmployeeID === payrollEntry.EmployeeID)
        }

        const loaded = this.$q.all([manualRequest, scheduleEventsLoaded])
        this.utilityService.showLoadingUntilResolved(
            this.utilityService.alertOnError(loaded, "Loading manual request form failed")
        );
        return loaded.then(([manualRequest, scheduleEvents]) => {
            return this._openManualSubmissionModal(manualRequest, payPeriod, filteredPayroll, scheduleEvents);
        })
            .then(() => {
                // Submission completed.
                return this.$state.reload();
            })
            .catch(ex => {
                if (ex === 'cancel') {
                    // No change was attempted.
                    return;
                }
                console.error(ex);
            });
    }

    _openManualSubmissionModal(manualSubmission, payPeriod, payroll, scheduleEvents) {
        return this.$uibModal.open({
            ...PayrollManualSubmissionModal,
            resolve: {
                manualRequest: () => manualSubmission,
                icdCorporations: () => [{
                    Id: payroll.IcdCorporationID,
                    ClientName: payroll.ClientName,
                    Owner: payroll.OwnerName
                }],
                payPeriods: () => [payPeriod],
                payrolls: () => [payroll],
                payrollID: () => payroll.PayrollID,
                schedule: () => scheduleEvents,
                payroll: () => payroll,
                payPeriod: () => payPeriod
            }
        }).result;

    }
}

function getAllPayPeriods(payPeriods: PayPeriodSections) {
    return payPeriods.admins.concat(payPeriods.contractors)
        .concat(payPeriods.sales);
}

function searchPayPeriod(payPeriod, searchTerm: string, payrollFilters: PayrollFilters) {
    if (payrollFilters.filterByIncompleteEmployeeRegistration && payPeriod.IsRegistrationComplete) {
        return false;
    }
    return containsInvariant(payPeriod.EmployeeFirstName + " " + payPeriod.EmployeeLastName,
        searchTerm);
}
